import { Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import { MdDelete } from 'react-icons/md'
import { currencyMask } from '../../../../utils/masks'
import NumberSpinner from '../../pontoDeVenda/qunatidadeProdutoTable'
import CurrencyField from '../../../../components/CurrencyField/CurrencyField'
import CardTable from '../../../../components/CardTable'
import { useCheckin } from './CheckinContext'

const nomeCell = ({ linhaOriginal, mostrarPaciente }) => (
  <Grid container>
    {mostrarPaciente && linhaOriginal?.nmPaciente && (
      <Grid item xs={12}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            fontSize: '0.55rem',
            lineHeight: 1.2,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {linhaOriginal.nmPaciente}
        </Typography>
      </Grid>
    )}
    <Grid item xs={12}>
      <Tooltip title={linhaOriginal?.dsProduto}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {linhaOriginal?.dsProduto}
        </Typography>
      </Tooltip>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        R$: {currencyMask(linhaOriginal?.vlUnitarioInicial || 0)}
      </Typography>
    </Grid>
  </Grid>
)

const ESTILO_5_COL = [
  { width: '36%', truncate: true },
  { width: '11%', textAlign: 'left' },
  { width: '15%', textAlign: 'center' },
  { width: '21%' },
  { width: '17%', textAlign: 'left' },
]

const ESTILO_6_COL = [
  { width: '25%', truncate: true },
  { width: '10%', textAlign: 'left' },
  { width: '22%', textAlign: 'center' },
  { width: '19%' },
  { width: '17%', textAlign: 'left' },
  { width: '7%', textAlign: 'center' },
]

const ESTILO_4_COL = [
  { width: '50%', truncate: true },
  { width: '15%', textAlign: 'center' },
  { width: '15%' },
  { width: '20%', textAlign: 'left' },
]

const LinhaEditavelCard = ({
  titulo,
  lista,
  onUpdateItem,
  comSolicitado,
  comExclusao,
  onDelete,
  preencherAltura,
  conteudoTopo,
  mostrarPaciente,
  compactarSimboloMoeda,
}) => {
  const { isSubmitting } = useFormikContext()
  const { isFinalizado } = useCheckin()
  const isDisabled = isSubmitting || isFinalizado

  const handleQtd = useCallback(
    (linha) => (quantidade) => {
      onUpdateItem(linha.idx, {
        vlTotal: quantidade > 0 ? linha.vlUnitario * quantidade : 0,
        qtProdutoAutorizado: quantidade,
      })
    },
    [onUpdateItem],
  )

  const handleUnitario = useCallback(
    (linha) => (unitario) => {
      onUpdateItem(linha.idx, {
        vlTotal:
          linha.qtProdutoAutorizado !== 0
            ? unitario * linha.qtProdutoAutorizado
            : 0,
        vlUnitario: unitario,
      })
    },
    [onUpdateItem],
  )

  const colunasBase = comSolicitado
    ? ['Nome', 'Solicitado', 'Autorizado', 'Unitário', 'Valor']
    : ['Nome', 'Autorizado', 'Unitário', 'Valor']

  const colunas = comExclusao ? [...colunasBase, ''] : colunasBase

  const mapear = (item) => {
    const vals = comSolicitado
      ? [
          item.dsProduto,
          item.qtProduto,
          item.qtProdutoAutorizado,
          item.vlUnitario,
          item.vlTotal,
        ]
      : [
          item.dsProduto,
          item.qtProdutoAutorizado,
          item.vlUnitario,
          item.vlTotal,
        ]
    if (comExclusao) vals.push('')
    return vals
  }

  const colAutorizado = comSolicitado ? 2 : 1
  const colUnitario = comSolicitado ? 3 : 2
  const colValor = comSolicitado ? 4 : 3
  const colDelete = comSolicitado ? 5 : 4

  const opcoes = [
    {
      alias: 0,
      customJsx: ({ linhaOriginal }) =>
        nomeCell({ linhaOriginal, mostrarPaciente }),
    },
    ...(comSolicitado
      ? [
          {
            alias: 1,
            customJsx: ({ linhaOriginal }) => (
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
              >
                {linhaOriginal?.qtProduto}
              </Typography>
            ),
          },
        ]
      : []),
    {
      alias: colAutorizado,
      customJsx: ({ linhaOriginal }) =>
        isFinalizado ? (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
          >
            {linhaOriginal?.qtProdutoAutorizado || 0}
          </Typography>
        ) : (
          <NumberSpinner
            size="small"
            disabled={isDisabled}
            onChange={handleQtd(linhaOriginal)}
            value={linhaOriginal?.qtProdutoAutorizado}
            min={0}
          />
        ),
    },
    {
      alias: colUnitario,
      customJsx: ({ linhaOriginal }) =>
        isFinalizado ? (
          <Typography
            variant="body2"
            sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
          >
            R$ {currencyMask(linhaOriginal?.vlUnitario || 0)}
          </Typography>
        ) : (
          <CurrencyField
            size="small"
            variant="outlined"
            disabled={isDisabled}
            compactCurrencySymbol={compactarSimboloMoeda}
            onChange={handleUnitario(linhaOriginal)}
            value={linhaOriginal.vlUnitario}
          />
        ),
    },
    {
      alias: colValor,
      customJsx: ({ linhaOriginal }) => (
        <Typography
          variant="body2"
          sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}
        >
          R$ {currencyMask(linhaOriginal.vlTotal)}
        </Typography>
      ),
    },
    ...(comExclusao
      ? [
          {
            alias: colDelete,
            customJsx: ({ linhaOriginal }) => (
              <IconButton
                size="small"
                disabled={isDisabled}
                onClick={() => onDelete?.(linhaOriginal.idx)}
              >
                <MdDelete fontSize={16} />
              </IconButton>
            ),
          },
        ]
      : []),
  ]

  const estiloColunas = comExclusao
    ? ESTILO_6_COL
    : comSolicitado
      ? ESTILO_5_COL
      : ESTILO_4_COL

  return (
    <CardTable
      titulo={titulo}
      dadosTabela={lista}
      colunas={colunas}
      colunasMoeda={[colUnitario, colValor]}
      mapear={mapear}
      estiloColunas={estiloColunas}
      opcoesColuna={opcoes}
      colunasTotal={[colValor]}
      rotuloTotal={`Total ${titulo}`}
      semBordas
      maxLinhas={3}
      alturaMinimaLinha={0}
      preencherAltura={preencherAltura}
      preencherCorpo={preencherAltura}
      conteudoTopo={conteudoTopo}
    />
  )
}

export default LinhaEditavelCard
